import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiesPolicy = () => {
  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-maroon flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">b8</span>
              </div>
              <span className="font-heading font-bold text-xl text-maroon">batchm8s</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-gray-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-maroon/10 rounded-xl">
              <Cookie className="h-8 w-8 text-maroon" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-gray-900">Cookies Policy</h1>
              <p className="text-gray-500">Last updated: February 20, 2026</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <h2>1. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They help websites remember your preferences and improve your browsing experience.
            </p>

            <h2>2. How We Use Cookies</h2>
            <p>
              batchm8s uses cookies and similar technologies for the following purposes:
            </p>

            <h3>2.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the website to function properly. They enable core functionality such as:
            </p>
            <ul>
              <li>User authentication and session management</li>
              <li>Security features and fraud prevention</li>
              <li>Remembering your login status</li>
              <li>Maintaining your preferences during a session</li>
            </ul>
            <p>
              <strong>These cookies cannot be disabled</strong> as they are essential for the Service to work.
            </p>

            <h3>2.2 Functional Cookies</h3>
            <p>
              These cookies enable enhanced functionality and personalization:
            </p>
            <ul>
              <li>Remembering your language preferences</li>
              <li>Storing your display preferences</li>
              <li>Remembering previously viewed events</li>
              <li>Pre-filling forms with your saved information</li>
            </ul>

            <h3>2.3 Analytics Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with our website:
            </p>
            <ul>
              <li>Counting visitors and tracking page views</li>
              <li>Understanding which features are most popular</li>
              <li>Identifying technical issues</li>
              <li>Measuring the effectiveness of our communications</li>
            </ul>

            <h2>3. Types of Cookies We Use</h2>
            <table className="min-w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Cookie Name</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">auth_token</td>
                  <td className="border border-gray-300 px-4 py-2">Stores your authentication token for login</td>
                  <td className="border border-gray-300 px-4 py-2">7 days</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">user_preferences</td>
                  <td className="border border-gray-300 px-4 py-2">Stores your display and notification preferences</td>
                  <td className="border border-gray-300 px-4 py-2">1 year</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">session_id</td>
                  <td className="border border-gray-300 px-4 py-2">Maintains your session while browsing</td>
                  <td className="border border-gray-300 px-4 py-2">Session</td>
                </tr>
              </tbody>
            </table>

            <h2>4. Third-Party Cookies</h2>
            <p>
              We may use third-party services that set their own cookies:
            </p>
            <ul>
              <li><strong>Payment Processors:</strong> PayPal may set cookies for payment processing and fraud prevention</li>
              <li><strong>Analytics:</strong> We may use analytics services to understand website usage</li>
            </ul>
            <p>
              These third parties have their own privacy and cookie policies, which we encourage you to review.
            </p>

            <h2>5. Local Storage</h2>
            <p>
              In addition to cookies, we may use local storage (HTML5) to store information on your device. This is used for:
            </p>
            <ul>
              <li>Caching data for faster loading</li>
              <li>Storing authentication tokens</li>
              <li>Remembering your preferences</li>
            </ul>

            <h2>6. Managing Cookies</h2>
            <p>
              You can control and manage cookies in various ways:
            </p>

            <h3>6.1 Browser Settings</h3>
            <p>
              Most web browsers allow you to control cookies through their settings. You can:
            </p>
            <ul>
              <li>View what cookies are stored on your device</li>
              <li>Delete all or specific cookies</li>
              <li>Block third-party cookies</li>
              <li>Block cookies from specific sites</li>
              <li>Block all cookies</li>
              <li>Clear all cookies when you close your browser</li>
            </ul>

            <h3>6.2 Browser-Specific Instructions</h3>
            <p>Here are links to cookie management instructions for popular browsers:</p>
            <ul>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-maroon hover:underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-maroon hover:underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-maroon hover:underline">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-maroon hover:underline">Microsoft Edge</a></li>
            </ul>

            <h3>6.3 Impact of Disabling Cookies</h3>
            <p>
              Please note that if you disable or delete cookies, some features of our Service may not function properly:
            </p>
            <ul>
              <li>You may need to log in more frequently</li>
              <li>Your preferences may not be saved</li>
              <li>Some features may become unavailable</li>
            </ul>

            <h2>7. Do Not Track</h2>
            <p>
              Some browsers have a "Do Not Track" feature that signals to websites that you do not want your online activities tracked. Our Service currently does not respond to "Do Not Track" signals.
            </p>

            <h2>8. Updates to This Policy</h2>
            <p>
              We may update this Cookies Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. We will post any changes on this page and update the "Last updated" date.
            </p>

            <h2>9. Contact Us</h2>
            <p>
              If you have any questions about our use of cookies, please contact us at:
            </p>
            <ul>
              <li>Email: info@batchm8s.com</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm">© 2026 batchm8s. Made with ❤️ for alumni everywhere.</p>
        </div>
      </footer>
    </div>
  );
};

export default CookiesPolicy;
